// Todo lo que el modo Diseño pide a la red — subir, firmar y generar
// (ADR-2026-09-17-las-imagenes-propias-del-diseno-se-suben). Sin React: lo llaman los
// paneles, la portada y el `ejecutar` de las herramientas del agente, por el MISMO camino.
//
// La generación es `creativo_plus` con referencias `adjunto` en el orden que da quien
// llama (la prenda primero — ADR-2026-09-17-la-instruccion-por-referencia-va-en-el-prompt),
// con `execution_id` del front para que la tanda se reanude tras recargar, y con el
// preload de las filas antes de resolver, como la columna de resultados y el lienzo.

import { BUCKET_ADJUNTOS_DEL_CHAT, signReferenceImage, uploadReferenceImage } from '../../agentAttachments';
import { priorizarReferencias, type ReferenciaEtiquetada } from '../../referenciasDeGeneracion';
import { firmarEnLote } from '../../../../utils/firmarEnLote';
import { preloadGeneratedImages } from '../../../../utils/preloadGeneratedImages';
import { analizarImagenes, describirPlantilla } from '../../replicarFotoApi';
import { atlasBackendFetch } from '../../../../lib/atlasBackend';
import type { AccionDeDiseno, ResultadoDeMezcla } from './disenoGuardado';
import { imagenConMarca } from './imagenConMarca';
import { describirMarca, type Marca } from './marcador';
import {
  MAX_IMAGENES_VISION,
  MODO_UTILIZADO_POR_ACCION,
  formatoParaAccion,
  parsearSalidaDelDirectorDeMezcla,
  parsearPromptsDelDirector,
  promptDirectorEnUso,
  promptDirectorMezcla,
} from './promptsDeDiseno';

export const BUCKET_DE_DISENO = BUCKET_ADJUNTOS_DEL_CHAT;

export async function subirImagenDeDiseno(file: File, companyId: string): Promise<{ bucket: string; path: string }> {
  const { storagePath } = await uploadReferenceImage(file, companyId);
  return { bucket: BUCKET_DE_DISENO, path: storagePath };
}

/**
 * Una base del catálogo se SUBE como si fuera una imagen del usuario: así todas las
 * propias tienen una sola forma ({bucket, path}, firmable) y el backend puede bajarla
 * también en desarrollo local, donde una URL pública del front no le llega.
 */
export async function subirBaseDelCatalogo(archivo: string, id: string, companyId: string): Promise<{ bucket: string; path: string }> {
  const res = await fetch(archivo);
  if (!res.ok) throw new Error('No se pudo leer la base del catálogo.');
  const blob = await res.blob();
  const file = new File([blob], `${id}.png`, { type: blob.type || 'image/png' });
  return subirImagenDeDiseno(file, companyId);
}

/**
 * VECTORIZAR A SVG (ADR-2026-09-22-el-plano-tecnico-se-baja-como-svg): el backend baja la
 * imagen del storage, la manda a Recraft y devuelve el SVG inline. Sirve para cualquier
 * imagen del lienzo, pero sólo vale la pena con un dibujo de línea —el plano técnico, una
 * mezcla "como la base", la prenda descrita—: una foto trazada es un archivo enorme y feo.
 * No cuesta créditos; el costo de Recraft es de un centavo por conversión.
 */
export async function vectorizarDeDiseno(origen: { bucket: string; path: string }): Promise<string> {
  const res = await atlasBackendFetch('/v1/images/vectorize', {
    image_bucket: origen.bucket,
    image_path: origen.path,
  });
  const data = (await res.json().catch(() => null)) as { svg?: string; error?: string } | null;
  if (!res.ok || !data?.svg) {
    throw new Error(data?.error || 'No se pudo convertir a SVG. Probá de nuevo.');
  }
  return data.svg;
}

const claveDe = (i: { bucket: string; path: string }): string => `${i.bucket}/${i.path}`;

/** Firma en lote por bucket. Clave: `${bucket}/${path}`. */
export async function firmarPropias(imgs: { bucket: string; path: string }[]): Promise<Map<string, string>> {
  const out = new Map<string, string>();
  const porBucket = new Map<string, string[]>();
  for (const i of imgs) porBucket.set(i.bucket, [...(porBucket.get(i.bucket) ?? []), i.path]);
  for (const [bucket, paths] of porBucket) {
    const firmas = await firmarEnLote(paths, bucket);
    for (const p of paths) {
      const u = firmas.get(p);
      if (u) out.set(claveDe({ bucket, path: p }), u);
    }
  }
  return out;
}

/** La URL firmada del original de una creación (`full/`), para mandarla de referencia. */
export async function urlDeFilaCompleta(companyId: string, fileName: string | null): Promise<string | null> {
  if (!fileName) return null;
  const path = `${companyId}/full/${fileName}`;
  const m = await firmarEnLote([path]);
  return m.get(path) ?? null;
}

/** Firma UNA propia (para una referencia suelta, fuera del lote de miniaturas). */
export const firmarPropia = (i: { bucket: string; path: string }): Promise<string | null> =>
  signReferenceImage(i.path, i.bucket);

export interface PedidoDeDiseno {
  accion: AccionDeDiseno;
  userId: string;
  companyId: string;
  prompt: string;
  /** URLs firmadas, en el orden en que se numeran (la prenda primero). Vacío en base-descrita. */
  referencias: string[];
  ratioDeLaPrenda: number;
  /** Cuántas imágenes: ya acotada por `cantidadParaAccion` (quien llama es `lanzar`). */
  cantidad: number;
  fondoBlanco?: boolean;
  /** Qué hace el motor con la base: "editar" la conserva (cadena de edición), "crear" la
   *  rehace. Lo decide el director de la mezcla
   *  (ADR-2026-09-18-el-director-de-la-mezcla-decide-la-tarea); sin él, la cadena de creación. */
  tarea?: 'crear' | 'editar' | 'componer' | 'explorar' | 'grafica' | 'cine';
  /** El `execution_id` a usar, cuando ya se le prometió a una tanda (la mezcla lo fija
   *  antes de que la visión escriba el prompt). Sin él se genera uno. */
  executionId?: string;
}

export interface PedidoLanzado {
  executionId: string;
  /** Todos los pedidos de la tanda (Mostrar en uso hace uno por foto); los demás, uno. */
  executionIds: string[];
  total: number;
  creditos: number;
  /** Ids de `imagenes_generadas`, ya precargadas. Rechaza con el error de la API. */
  resultado: Promise<string[]>;
}

export function pedirGeneracionDeDiseno(p: PedidoDeDiseno): PedidoLanzado {
  const executionId = p.executionId ?? crypto.randomUUID();
  const total = p.cantidad;
  const referenceImages: ReferenciaEtiquetada[] = priorizarReferencias(
    p.referencias.map((url) => ({ url, tipo: 'adjunto' as const })),
  );
  const resultado = (async (): Promise<string[]> => {
    // `import()` dinámico, como Prompt libre: `agentGenerate` arrastra todo el motor.
    const { generateFreeformImage } = await import('../../agentGenerate');
    const { imageIds } = await generateFreeformImage({
      userId: p.userId,
      prompt: p.prompt,
      formatSlug: formatoParaAccion(p.accion, p.ratioDeLaPrenda),
      cantidad: total,
      companyId: p.companyId,
      executionId,
      imageSize: '1K',
      modoUtilizado: MODO_UTILIZADO_POR_ACCION[p.accion],
      ...(p.fondoBlanco ? { fondoBlanco: true } : {}),
      ...(p.tarea ? { tarea: p.tarea } : {}),
      ...(referenceImages.length > 0 ? { referenceImages } : {}),
    });
    try {
      window.dispatchEvent(new Event('atlas:credits-updated'));
    } catch {
      /* noop */
    }
    return conPreload(imageIds.map(String), p.companyId);
  })();
  return { executionId, executionIds: [executionId], total, creditos: total, resultado };
}

/** Preload de las filas antes de resolver, como la columna de resultados y el lienzo. */
async function conPreload(ids: string[], companyId: string): Promise<string[]> {
  try {
    const pre = await preloadGeneratedImages(ids, companyId);
    if (pre.length > 0) return pre.map((x) => String(x.id));
  } catch {
    // Sin preload los ids igual sirven: la miniatura se pide por su cuenta.
  }
  return ids;
}

/**
 * MOSTRAR EN USO (ADR-2026-09-18-mostrar-en-uso-escribe-sus-prompts): dos pasos.
 * 1. La VISIÓN mira la prenda (`/v1/media/analyze`, gratis, `describirPlantilla`) y
 *    escribe un prompt por foto con la estructura de `foto-con-modelo`
 *    (`promptDirectorEnUso`): escena, plano y pose distintas en cada una.
 * 2. Un pedido `creativo_plus` de UNA imagen por prompt, todos con la prenda como única
 *    referencia (adjunto) y cada uno con su `execution_id`: la tanda se reanuda por todos.
 * Resuelve con las que salieron; si no salió ninguna, rechaza con el primer error.
 */
export function pedirFotosEnUso(p: {
  userId: string;
  companyId: string;
  /** URL firmada del original de la prenda: la visión sólo acepta firmadas de nuestro storage. */
  urlPrenda: string;
  instruccion: string;
  cantidad: number;
  /** Los ids de una tanda que se RELANZA (ADR-2026-09-18-una-tanda-en-vuelo-se-relanza-si-el-pedido-no-llego). */
  executionIds?: string[];
}): PedidoLanzado {
  // Los ids se fijan ANTES de que la visión escriba: así la tanda existe desde el primer
  // instante (recuadros en el lienzo, hoja cerrada) y se puede reanudar aunque la página
  // se recargue mientras se escriben los prompts
  // (ADR-2026-09-18-los-recuadros-aparecen-antes-que-el-prompt). Si la visión devuelve
  // menos prompts que `cantidad`, sobran ids y sobran recuadros: se resuelve con las que
  // salieron y los recuadros de más se van.
  const executionIds =
    p.executionIds && p.executionIds.length === p.cantidad
      ? p.executionIds
      : Array.from({ length: p.cantidad }, () => crypto.randomUUID());
  const referenceImages: ReferenciaEtiquetada[] = priorizarReferencias([{ url: p.urlPrenda, tipo: 'adjunto' as const }]);
  const resultado = (async (): Promise<string[]> => {
    const salida = await describirPlantilla({ url: p.urlPrenda, prompt: promptDirectorEnUso(p.cantidad, p.instruccion) });
    const prompts = parsearPromptsDelDirector(salida, p.cantidad);
    if (prompts.length === 0) throw new Error('No se pudieron escribir los prompts de las fotos. Probá de nuevo.');
    const { generateFreeformImage } = await import('../../agentGenerate');
    const pedidos = await Promise.allSettled(
      prompts.map((prompt, i) =>
        generateFreeformImage({
          userId: p.userId,
          prompt,
          formatSlug: formatoParaAccion('en-uso', 1),
          cantidad: 1,
          companyId: p.companyId,
          executionId: executionIds[i],
          imageSize: '1K',
          modoUtilizado: MODO_UTILIZADO_POR_ACCION['en-uso'],
          referenceImages,
        }),
      ),
    );
    try {
      window.dispatchEvent(new Event('atlas:credits-updated'));
    } catch {
      /* noop */
    }
    const ids = pedidos.flatMap((r) => (r.status === 'fulfilled' ? r.value.imageIds.map(String) : []));
    if (ids.length === 0) {
      const fallo = pedidos.find((r): r is PromiseRejectedResult => r.status === 'rejected');
      throw fallo?.reason instanceof Error ? fallo.reason : new Error('No se pudo generar. Intentá nuevamente.');
    }
    return conPreload(ids, p.companyId);
  })();
  return { executionId: executionIds[0], executionIds, total: p.cantidad, creditos: p.cantidad, resultado };
}

/**
 * La imagen que mira la visión: si tiene marca, una copia con lo pintado encima, subida
 * y firmada (la visión sólo acepta firmadas de nuestro storage). Si algo falla (CORS,
 * canvas, subida) se cae a la imagen limpia: la marca igual viaja en palabras en el
 * prompt del director.
 */
async function urlParaLaVision(url: string, marca: Marca | undefined, companyId: string): Promise<string> {
  if (!marca) return url;
  try {
    const subida = await subirImagenDeDiseno(await imagenConMarca(url, marca), companyId);
    return (await firmarPropia(subida)) ?? url;
  } catch {
    return url;
  }
}

/**
 * MEZCLAR (ADR-2026-09-18-mezclar-escribe-su-prompt): la VISIÓN mira la base y las partes
 * (hasta 5 imágenes: la base y las primeras 4 partes; las demás sólo por su instrucción)
 * con lo que el diseñador escribió de cada una, y escribe el prompt de la mezcla
 * (`promptDirectorMezcla`). Después, UN pedido `creativo_plus` con ese prompt y TODAS las
 * imágenes como referencias, la base primero, en el mismo orden que se numeraron.
 * Las MARCAS del Marcador (ADR-2026-09-21-el-marcador-de-la-mezcla) llegan a la visión
 * pintadas sobre una copia y en palabras; el generador recibe las imágenes limpias y el
 * prompt ya habla de la zona en términos de la prenda.
 */
export function pedirMezcla(p: {
  userId: string;
  companyId: string;
  urlBase: string;
  marcaBase?: Marca;
  partes: { url: string; instruccion: string; marca?: Marca }[];
  instruccionGeneral: string;
  /** Qué tipo de imagen sale (ADR-2026-09-18-la-mezcla-elige-realista-o-como-la-base). */
  resultado: ResultadoDeMezcla;
  cantidad: number;
  ratioDeLaPrenda: number;
  /** El id de una tanda que se RELANZA. */
  executionId?: string;
}): PedidoLanzado {
  // El id se fija antes de que la visión escriba, por lo mismo que en `pedirFotosEnUso`.
  const executionId = p.executionId ?? crypto.randomUUID();
  const veLaVision = Math.max(0, MAX_IMAGENES_VISION - 1);
  const meta = promptDirectorMezcla(
    p.partes.map((parte, i) => ({
      instruccion: parte.instruccion,
      laVeLaVision: i < veLaVision,
      ...(parte.marca ? { marca: describirMarca(parte.marca) } : {}),
    })),
    p.instruccionGeneral,
    p.resultado,
    p.marcaBase ? describirMarca(p.marcaBase) : undefined,
  );
  const resultado = (async (): Promise<string[]> => {
    const urlsParaLaVision = await Promise.all([
      urlParaLaVision(p.urlBase, p.marcaBase, p.companyId),
      ...p.partes.slice(0, veLaVision).map((x) => urlParaLaVision(x.url, x.marca, p.companyId)),
    ]);
    const salida = parsearSalidaDelDirectorDeMezcla(await analizarImagenes({ urls: urlsParaLaVision, prompt: meta }));
    if (!salida) throw new Error('No se pudo escribir el prompt de la mezcla. Probá de nuevo.');
    // El director decide si la base se edita o se rehace, y si va sobre blanco (plano
    // técnico): con "editar" el backend usa la cadena que conserva la imagen.
    return pedirGeneracionDeDiseno({
      accion: 'mezcla',
      userId: p.userId,
      companyId: p.companyId,
      prompt: salida.prompt,
      tarea: salida.tarea,
      fondoBlanco: salida.fondoBlanco,
      referencias: [p.urlBase, ...p.partes.map((x) => x.url)],
      ratioDeLaPrenda: p.ratioDeLaPrenda,
      cantidad: p.cantidad,
      executionId,
    }).resultado;
  })();
  return { executionId, executionIds: [executionId], total: p.cantidad, creditos: p.cantidad, resultado };
}
